import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ContentProvider } from "@/context/ContentContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Auth from "@/pages/Auth";
import Training from "@/pages/Training";
import League from "@/pages/League";
import LeagueRegister from "@/pages/LeagueRegister";
import LeagueOverview from "@/pages/LeagueOverview";
import Store from "@/pages/Store";
import Community from "@/pages/Community";
import Profile from "@/pages/Profile";
import RoadToNationals from "@/pages/RoadToNationals";
import PBRRankings from "@/pages/PBRRankings";
import Book from "@/pages/Book";
import Admin from "@/pages/Admin";
import Wallet from "@/pages/Wallet";
import CoachesLounge from "@/pages/CoachesLounge";
import Landing from "@/pages/Landing";
import Tournaments from "@/pages/Tournaments";
import ChampionsWall from "@/pages/ChampionsWall";
import CoachesCorner from "@/pages/CoachesCorner";
import PlayerTournamentHub from "@/pages/PlayerTournamentHub";
import TournamentDetail from "@/pages/TournamentDetail";
import FAQ from "@/pages/FAQ";
import ResetPassword from "@/pages/ResetPassword";
import GoogleCallback from "@/pages/GoogleCallback";
import CreditPolicy from "@/pages/CreditPolicy";
import CourtBoard from "@/pages/CourtBoard";
import Live from "@/pages/Live";
import Scorekeeper from "@/pages/Scorekeeper";
import TdCourtBoard from "@/pages/TdCourtBoard";
import CourtLayout from "@/pages/CourtLayout";
import GalleryLanding from "@/pages/GalleryLanding";
import GalleryAlbum from "@/pages/GalleryAlbum";
import GoldLetter from "@/pages/GoldLetter";
import Certificate from "@/pages/Certificate";
import TournamentReport from "@/pages/TournamentReport";
import TournamentIntel from "@/pages/TournamentIntel";
import GrailGallery from "@/pages/GrailGallery";
import PublicEvents from "@/pages/PublicEvents";
import TourStandings from "@/pages/TourStandings";
import TourSignup from "@/pages/TourSignup";
import PublicEventDetail from "@/pages/PublicEventDetail";
import CityTournaments from "@/pages/CityTournaments";
import EventRecap from "@/pages/EventRecap";
import PublicBracket from "@/pages/PublicBracket";
import PlayersLab from "@/pages/PlayersLab";
import BrainTraining from "@/pages/BrainTraining";
import BrainPublic from "@/pages/BrainPublic";
import PublicRoster from "@/pages/PublicRoster";
import Storefront, { ShopsDirectory } from "@/pages/Storefront";
import ProductPage from "@/pages/ProductPage";
import ReturnPolicyPage from "@/pages/ReturnPolicyPage";
import LegalPage from "@/components/LegalPage";
import { PRIVACY, TERMS } from "@/lib/legal";
import Upgrade from "@/pages/Upgrade";
import Vault from "@/pages/Vault";
import CardWars from "@/pages/CardWars";
import CardVerify from "@/pages/CardVerify";
import AthleteInvite from "@/pages/AthleteInvite";
import { PlayerCardProvider } from "@/context/PlayerCardContext";
import { SessionHeartbeat } from "@/components/SessionHeartbeat";
import { JourneyTracker } from "@/components/JourneyTracker";
import RouteSeo from "@/components/RouteSeo";
import { CardZoomViewer } from "@/components/CardZoom";

const isBullpinDomain = window.location.hostname.includes("picklebullpin");

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="min-h-screen flex items-center justify-center bg-pb-base text-pb-red font-display text-2xl tracking-widest uppercase animate-pulse">Loading Bullpen…</div>;
  if (!user) {
    const seenWelcome = localStorage.getItem("pb_seen_welcome");
    return <Navigate to={seenWelcome ? "/auth" : "/welcome"} replace />;
  }
  return children;
}

function RootGate() {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="min-h-screen flex items-center justify-center bg-pb-base text-pb-red font-display text-2xl tracking-widest uppercase animate-pulse">Loading Bullpen…</div>;
  if (!user) return <Landing />;
  return <Navigate to="/profile" replace />;
}

function App() {
  if (isBullpinDomain) {
    return (
      <div className="App">
        <Landing />
      </div>
    );
  }
  return (
    <div className="App grain">
      <CardZoomViewer />
      <AuthProvider>
        <CartProvider>
        <ContentProvider>
        <BrowserRouter>
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          {window.location.hash.includes("session_id=") ? <GoogleCallback /> : (<>
          <RouteSeo />
          <Routes>
            <Route path="/" element={<RootGate />} />
            <Route path="/welcome" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/live" element={<Live />} />
            <Route path="/tour" element={<TourStandings />} />
            <Route path="/toursignup" element={<TourSignup />} />
            <Route path="/court-board" element={<CourtBoard />} />
            <Route path="/court-board/:tid" element={<CourtBoard />} />
            <Route path="/td-court-board/:id" element={<TdCourtBoard />} />
            <Route path="/brackets/:tid" element={<PublicBracket />} />
            <Route path="/rosters" element={<PublicRoster />} />
            <Route path="/rosters/:tid" element={<PublicRoster />} />
            <Route path="/court-layout/:tid" element={<CourtLayout />} />
            <Route path="/gallery" element={<GalleryLanding />} />
            <Route path="/gallery/:aid" element={<GalleryAlbum />} />
            <Route path="/certificate/:cid" element={<Certificate />} />
            <Route path="/grails" element={<GrailGallery />} />
            <Route path="/card/verify/:serial" element={<CardVerify />} />
            <Route path="/invite/:token" element={<AthleteInvite />} />
            <Route path="/events" element={<PublicEvents />} />
            <Route path="/brain-training" element={<BrainPublic />} />
            <Route path="/events/:id" element={<PublicEventDetail />} />
            <Route path="/events/:id/recap" element={<EventRecap />} />
            <Route path="/pickleball-tournaments/:city" element={<CityTournaments />} />
            <Route path="/pickleball-tournaments" element={<Navigate to="/events" replace />} />
            <Route path="/shops" element={<ShopsDirectory />} />
            <Route path="/shops/:id" element={<Storefront />} />
            <Route path="/shop/item/:slug" element={<ProductPage />} />
            <Route path="/return-policy" element={<ReturnPolicyPage />} />
            <Route path="/returnpolicy" element={<Navigate to="/return-policy" replace />} />
            <Route path="/return policy" element={<Navigate to="/return-policy" replace />} />
            <Route path="/return_policy" element={<Navigate to="/return-policy" replace />} />
            <Route path="/privacy-policy" element={<LegalPage doc={PRIVACY} testid="privacy-page" />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms-of-service" element={<LegalPage doc={TERMS} testid="terms-page" />} />
            <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/tos" element={<Navigate to="/terms-of-service" replace />} />
            <Route element={<Protected><PlayerCardProvider><SessionHeartbeat /><JourneyTracker /><Layout /></PlayerCardProvider></Protected>}>
              <Route path="/training" element={<Training />} />
              <Route path="/league" element={<League />} />
              <Route path="/league/register" element={<LeagueRegister />} />
              <Route path="/league/overview" element={<LeagueOverview />} />
              <Route path="/book" element={<Book />} />
              <Route path="/coaches-corner" element={<CoachesCorner />} />
              <Route path="/store" element={<Store />} />
              <Route path="/community" element={<Community />} />
              <Route path="/tournaments" element={<Tournaments />} />
              <Route path="/champions" element={<ChampionsWall />} />
              <Route path="/tournaments/hub" element={<PlayerTournamentHub />} />
              <Route path="/tournaments/:id" element={<TournamentDetail />} />
              <Route path="/tournaments/:id/report" element={<TournamentReport />} />
              <Route path="/admin/tournament-intel" element={<TournamentIntel />} />
              <Route path="/letters/:lid" element={<GoldLetter />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/lab" element={<PlayersLab />} />
              <Route path="/brain" element={<BrainTraining />} />
              <Route path="/road-to-nationals" element={<RoadToNationals />} />
              <Route path="/road-to-nationals/:pid" element={<RoadToNationals />} />
              <Route path="/pbr-rankings" element={<PBRRankings />} />
              <Route path="/upgrade" element={<Upgrade />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/vault" element={<Vault />} />
              <Route path="/card-wars" element={<CardWars />} />
              <Route path="/credit-policy" element={<CreditPolicy />} />
              <Route path="/coaches-lounge" element={<CoachesLounge />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/scorekeeper" element={<Scorekeeper />} />
            </Route>
            <Route path="*" element={<Navigate to="/profile" replace />} />
          </Routes>
          </>)}
        </BrowserRouter>
        </ContentProvider>
        </CartProvider>
        <Toaster position="top-right" theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
